import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import AppModal from "../common/AppModal";
import StatusChip from "../common/StatusChip";
import DetailItem from "../common/DetailItem";
import AppCircularProgress from "../common/AppCircularProgress";
import { BranchResponseDto } from "../../shared/dtos/branch/BranchResponseDto";

interface ViewBranchModalProps {
  open: boolean;
  onClose: () => void;
  branch: BranchResponseDto | null;
  loading: boolean;
  error: string | null;
}

const ViewBranchModal: React.FC<ViewBranchModalProps> = ({
  open,
  onClose,
  branch,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
          <AppCircularProgress />
        </Box>
      );
    }
    if (error) {
      return <Typography sx={{ color: "error.main" }}>{error}</Typography>;
    }
    if (!branch) {
      return (
        <Typography sx={{ color: "text.secondary" }}>
          No branch data available.
        </Typography>
      );
    }

    const { address } = branch;
    const fullAddress = address
      ? `${address.streetLine1}, ${address.city}, ${address.country}`
      : "N/A";

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Branch Name" value={branch.name} />
          <DetailItem label="Branch Code" value={branch.branchCode} />
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 0.5 }}
            >
              Status
            </Typography>
            <StatusChip status={branch.branchStatus} />
          </Box>
          <DetailItem label="Principal" value={branch.principalName} />
          <DetailItem label="Contact Phone" value={branch.contactPhone} />
          <DetailItem label="Contact Email" value={branch.contactEmail} />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, color: "text.secondary" }}
          >
            Physical Address
          </Typography>
        </Divider>

        <Typography variant="body1" sx={{ color: "text.primary" }}>
          {fullAddress}
        </Typography>
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Branch Details" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewBranchModal;
